import React, { useState } from "react";
import { addCareersData } from "../../services/data.service";
import { content as contentDefault, nonTeaching as nonTeachingDefault } from "../../data/careers";
import { TextField, SelectField, TextareaField, FileField } from "./fields";
import Stepper from "./Stepper";
import SuccessScreen from "./SuccessScreen";
import ReviewModal from "./ReviewModal";
import ConfirmDialog from "./ConfirmDialog";
import { IconUser, IconGraduation, IconBriefcase, IconEdit } from "./icons";
import {
  isEmpty, isValidDotsHyphens, isValidNumber, emailRe, mobileRe,
  makeRefId, dateBounds, validateResume,
} from "./helpers";

const stepIcons = [IconUser, IconGraduation, IconBriefcase];

const NonTeachingForm = ({ onExit, content = contentDefault, nonTeaching = nonTeachingDefault }) => {
  const { stepTitles, qualifications, posts } = nonTeaching;
  const ALL_STEPS = stepTitles.map((_, i) => i + 1);
  const { max: maxDate, min: minDate, currentYear } = dateBounds();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [errors, setErrors] = useState({});
  const [showReview, setShowReview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewErr, setReviewErr] = useState("");
  const [form, setForm] = useState({
    Applicationid: makeRefId("NT"), rectype: "Non Teaching",
    fullname: "", dob: "", gender: "", phone: "", email: "", address: "",
    qualification: "", nt_specialization: "", nt_institution: "", nt_yearpass: "", nt_cgpa: "",
    selectpost: "", has_experience: "", total_exp: "", prev_org: "", attachment: "",
  });

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };
  const bind = (name, filter) => ({
    value: form[name], error: errors[name],
    onChange: (e) => { const v = e.target.value; if (!filter || filter(v)) set(name, v); },
  });

  // Validate the given step(s). Defaults to current step; review passes ALL_STEPS.
  const validate = (stepsToCheck = [step]) => {
    const e = {};
    if (stepsToCheck.includes(1)) {
      if (isEmpty(form.fullname)) e.fullname = "Full Name is required";
      else if (form.fullname.trim().length < 4) e.fullname = "At least 4 characters";
      if (isEmpty(form.dob)) e.dob = "Date of Birth is required";
      if (isEmpty(form.gender)) e.gender = "Select Gender";
      if (!mobileRe.test(form.phone)) e.phone = "Valid 10-digit mobile is required";
      if (!emailRe.test(form.email)) e.email = "Valid email is required";
      if (isEmpty(form.address)) e.address = "Address is required";
      else if (form.address.trim().length < 10) e.address = "At least 10 characters";
    }
    if (stepsToCheck.includes(2)) {
      if (isEmpty(form.qualification)) e.qualification = "Select Highest Qualification";
      if (isEmpty(form.nt_specialization)) e.nt_specialization = "Specialization is required";
      if (isEmpty(form.nt_institution)) e.nt_institution = "Institution / University is required";
      if (isEmpty(form.nt_yearpass)) e.nt_yearpass = "Year of Passing is required";
      else if (!/^\d{4}$/.test(form.nt_yearpass)) e.nt_yearpass = "Year must be 4 digits";
      else if (+form.nt_yearpass > currentYear) e.nt_yearpass = "Future years not allowed";
      else if (+form.nt_yearpass < content.yearMin) e.nt_yearpass = `Enter a valid year after ${content.yearMin}`;
      if (isEmpty(form.nt_cgpa)) e.nt_cgpa = "Percentage / CGPA is required";
      else if (isNaN(form.nt_cgpa) || form.nt_cgpa < 0 || form.nt_cgpa > 100) e.nt_cgpa = "Enter a value between 0 and 100";
      if (isEmpty(form.selectpost)) e.selectpost = "Select Post Applied For";
    }
    if (stepsToCheck.includes(3)) {
      if (isEmpty(form.has_experience)) e.has_experience = "Please select an option";
      if (form.has_experience === "Yes") {
        if (isEmpty(form.total_exp)) e.total_exp = "Total Experience is required";
        else if (isNaN(form.total_exp) || +form.total_exp < 0) e.total_exp = "Enter a valid number";
        else if (+form.total_exp >= 50) e.total_exp = "Cannot exceed 50 years";
        if (isEmpty(form.prev_org)) e.prev_org = "Previous Organization is required";
        else if (form.prev_org.trim().length < 3) e.prev_org = "At least 3 characters";
      }
      const fileErr = validateResume(form.attachment, content.file.maxKB);
      if (fileErr) e.attachment = fileErr;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep((s) => s + 1); };
  const back = () => (step === 1 ? onExit && onExit() : setStep((s) => s - 1));

  const handleWizardSubmit = (ev) => {
    ev.preventDefault();
    if (step === ALL_STEPS.length) { validate(ALL_STEPS); setReviewErr(""); setSubmitErr(""); setShowReview(true); }
    else next();
  };

  const onReviewSubmit = () => {
    if (validate(ALL_STEPS)) { setReviewErr(""); setConfirmOpen(true); }
    else setReviewErr("Please correct the highlighted fields before submitting.");
  };

  const doSubmit = async () => {
    const { attachment, ...rest } = form;
    const data = new FormData();
    data.append("formData", JSON.stringify(rest));
    if (attachment) data.append("attachment", attachment);
    try {
      setLoading(true);
      setSubmitErr("");
      const res = await addCareersData(data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res) {
        setConfirmOpen(false);
        setShowReview(false);
        setSubmitted(true);
      } else {
        setConfirmOpen(false);
        setSubmitErr("Something went wrong. Please try again.");
      }
    } catch (err) {
      setConfirmOpen(false);
      setSubmitErr(err.response?.data?.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fields per step — reused by the wizard and the review modal.
  const sectionFields = (n) => {
    if (n === 1) return (
      <div className="crs-grid">
        <TextField full label="Full Name" name="fullname" required placeholder="Enter your full name" {...bind("fullname", isValidDotsHyphens)} />
        <TextField label="Date of Birth" name="dob" type="date" required min={minDate} max={maxDate} {...bind("dob")} />
        <SelectField label="Gender" name="gender" required options={content.genders} {...bind("gender")} />
        <TextField label="Mobile Number" name="phone" type="tel" inputMode="numeric" required maxLength={10} placeholder="10-digit mobile" {...bind("phone", (v) => isValidNumber(v, 10))} />
        <TextField label="Email ID" name="email" type="email" required placeholder="you@example.com" {...bind("email")} />
        <TextareaField label="Address" name="address" required placeholder="Full address…" {...bind("address")} />
      </div>
    );
    if (n === 2) return (
      <div className="crs-grid">
        <SelectField label="Highest Qualification" name="qualification" required options={qualifications} {...bind("qualification")} />
        <TextField label="Specialization" name="nt_specialization" required placeholder="Specialization" {...bind("nt_specialization")} />
        <TextField full label="Institution / University" name="nt_institution" required placeholder="Institution / University" {...bind("nt_institution")} />
        <TextField label="Year of Passing" name="nt_yearpass" type="tel" inputMode="numeric" required maxLength={4} placeholder="YYYY" {...bind("nt_yearpass", (v) => isValidNumber(v, 4))} />
        <TextField label="Percentage / CGPA" name="nt_cgpa" required placeholder="e.g. 78 or 8.2" {...bind("nt_cgpa", (v) => v === "" || /^\d{0,3}(\.\d{0,2})?$/.test(v))} />
        <SelectField full label="Post Applied For" name="selectpost" required options={posts} {...bind("selectpost")} />
      </div>
    );
    if (n === 3) return (
      <div className="crs-grid">
        <SelectField full label="Do you have experience?" name="has_experience" required options={content.yesNo} {...bind("has_experience")} />
        {form.has_experience === "Yes" && (
          <>
            <TextField label="Total Experience (years)" name="total_exp" type="tel" inputMode="numeric" required maxLength={2} placeholder="e.g. 3" {...bind("total_exp", (v) => isValidNumber(v, 2))} />
            <TextField label="Previous Organization" name="prev_org" required placeholder="Organization name" {...bind("prev_org")} />
          </>
        )}
        <FileField
          full label="Upload Resume" name="attachment" required
          file={form.attachment instanceof File ? form.attachment : null}
          accept={content.file.accept} note={content.file.note} error={errors.attachment}
          onChange={(e) => set("attachment", e.target.files[0])}
        />
      </div>
    );
    return null;
  };

  if (submitted) return <SuccessScreen refid={form.Applicationid} formData={form} rectype="Non Teaching" />;

  const StepIcon = stepIcons[step - 1];

  return (
    <>
      <div className="crs-card">
        <div className="crs-card__head">
          <div>
            <p className="crs-card__head-eyebrow">Non-Teaching Recruitment · Step {step} / {stepTitles.length}</p>
            <h2>{stepTitles[step - 1]}</h2>
          </div>
          <span className="crs-card__head-icon"><StepIcon /></span>
        </div>
        <div className="crs-card__body">
          <Stepper titles={stepTitles} current={step} />
          <form onSubmit={handleWizardSubmit} noValidate>
            {!showReview && sectionFields(step)}

            <div className="crs-nav">
              <button type="button" className="btn-ed btn-ed--ghost" onClick={back}>
                {step === 1 ? "Cancel" : "Back"}
              </button>
              <div className="crs-nav__spacer" />
              {step < stepTitles.length ? (
                <button key="nav-next" type="button" className="btn-ed btn-ed--primary" onClick={next}>Next</button>
              ) : (
                <button key="nav-review" type="submit" className="btn-ed btn-ed--primary">Review &amp; Submit</button>
              )}
            </div>
          </form>
        </div>
      </div>

      <ReviewModal
        open={showReview}
        title="Review Your Application"
        subtitle={`Non-Teaching · ${form.Applicationid}`}
        onClose={() => setShowReview(false)}
      >
        <p className="crs-review-hint">
          <IconEdit /> Review your details below — click any field to edit. Scroll down to submit.
        </p>
        {stepTitles.map((t, i) => (
          <section className="crs-review-section" key={i}>
            <h3 className="crs-review-section__title">
              <span className="crs-review-section__no">{i + 1}</span>{t}
            </h3>
            {sectionFields(i + 1)}
          </section>
        ))}
        {(reviewErr || submitErr) && <div className="crs-alert" role="alert">{reviewErr || submitErr}</div>}
        <div className="crs-review-foot">
          <button type="button" className="btn-ed btn-ed--ghost" onClick={() => setShowReview(false)}>Back to Form</button>
          <button type="button" className="btn-ed btn-ed--primary" onClick={onReviewSubmit}>Submit Application</button>
        </div>
      </ReviewModal>

      <ConfirmDialog
        open={confirmOpen}
        loading={loading}
        title="Submit your application?"
        message="Please confirm all your details are correct. You won't be able to edit this application after submitting."
        confirmLabel="Yes, Submit"
        cancelLabel="Review Again"
        onConfirm={doSubmit}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default NonTeachingForm;
